import { getSession } from 'next-auth/react';
import GcsUpload from 'gcs-browser-upload';
import { tenantStore } from '@/lib/tenantStore';
import { logger } from '@/lib/logger';

export type FileUploadStatus = 'pending_scan' | 'scan_passed' | 'scan_failed';

export interface FileUploadDto {
  id: string;
  jobConfigId: string | null;
  originalFilename: string;
  fileType: string;
  fileSizeBytes: number | null;
  uploadStatus: FileUploadStatus;
  cleanGcsPath: string | null;
  createdAt: string;
  updatedAt: string;
}

export type JobSetupState =
  | 'draft'
  | 'uploading'
  | 'upload_complete'
  | 'scan_failed'
  | 'discovering'
  | 'discovery_failed'
  | 'layers_discovered'
  | 'configured'
  | 'running'
  | 'completed'
  | 'run_failed';

export interface DiscoveredField {
  name: string;
  inferredType: string;
}

export type CoerceType =
  | 'STRING'
  | 'INT64'
  | 'FLOAT64'
  | 'NUMERIC'
  | 'BOOL'
  | 'DATE'
  | 'DATETIME'
  | 'TIMESTAMP'
  | 'GEOGRAPHY'
  | 'BYTES';

export type QueryOperator = '=' | '!=' | '>' | '<' | '>=' | '<=' | 'LIKE' | 'IS NULL' | 'IN';

export type AssetDataType = 'assets' | 'breaks';

export interface ColumnMapping {
  originalName: string;
  aliasName: string;
  coerceType: CoerceType;
  included: boolean;
  required: boolean;
  isMergeKey: boolean;
}

export interface QueryRule {
  column: string;
  operator: QueryOperator;
  value: string;
}

export interface QueryGroup {
  logic: 'OR';
  rules: QueryRule[];
}

export interface QueryFilter {
  logic: 'AND' | 'OR';
  rules: QueryRule[];
  groups?: QueryGroup[];
}

export interface LayerConfigDto {
  id: string;
  isSelected: boolean;
  userConfigured: boolean;
  assetDataType: AssetDataType | null;
  friendlyName: string | null;
  columnMappings: ColumnMapping[];
  queryFilter: QueryFilter | null;
  updatedAt: string;
}

export interface LayerWithConfigDto {
  id: string;
  fileUploadId: string;
  layerName: string;
  geometryType: string | null;
  featureCount: number | null;
  discoveredFields: DiscoveredField[];
  config: LayerConfigDto | null;
}

export interface PreviewColumn {
  name: string;
  coerceType: string;
}

export interface PreviewDto {
  columns: PreviewColumn[];
  rows: Array<Record<string, string>>;
  totalRowCount: number | null;
  filterApplied: boolean;
}

export interface SaveLayerConfigPayload {
  isSelected: boolean;
  assetDataType: AssetDataType | null;
  friendlyName: string | null;
  columnMappings: ColumnMapping[];
  queryFilter: QueryFilter | null;
}

export interface PaginatedList<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface JobConfigDto {
  id: string;
  name: string;
  description: string | null;
  inputType: string;
  outputType: string;
  setupState: JobSetupState;
  status: string;
  deleteOnRun: boolean;
  skipOnError: boolean;
  emailOnSuccess: boolean;
  emailOnFailure: boolean;
  createdAt: string;
  updatedAt: string;
  bqDurationMs: number | null;
  tablesExist: boolean;
}

interface UploadInitResponse {
  job: JobConfigDto;
  file: FileUploadDto;
  uploadUrl: string;
}

// Set at client startup by ClientConfigProvider from the server-side DATA_CONDUIT_URL env var.
let DATA_CONDUIT_BASE = 'http://localhost:3001';
export function setDataConduitBaseUrl(url: string) {
  DATA_CONDUIT_BASE = url;
}

async function buildHeaders(contentType?: string): Promise<Record<string, string>> {
  const session = await getSession();
  const tenant = tenantStore.getTenant();

  const headers: Record<string, string> = {};
  if (contentType) headers['Content-Type'] = contentType;
  if (tenant) headers['tenant'] = tenant;
  if (session?.accessToken) headers['Authorization'] = `Bearer ${session.accessToken}`;
  return headers;
}

async function dcFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${DATA_CONDUIT_BASE}${path}`, options);

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    logger.error('Data conduit request failed', 'DataConduitClient', {
      status: response.status,
      path,
      errorPreview: text.substring(0, 200),
    });
    const err = new Error(text || `Request failed: ${response.statusText}`);
    (err as Error & { status: number }).status = response.status;
    throw err;
  }

  if (response.status === 204) return null as T;
  return response.json();
}

export async function uploadFile(
  file: File,
  onProgress?: (percent: number) => void
): Promise<JobConfigDto> {
  // Step 1: Init — API creates the job + file record and returns a GCS resumable upload URL
  const headers = await buildHeaders('application/json');
  const {
    job,
    file: fileDto,
    uploadUrl,
  } = await dcFetch<UploadInitResponse>('/api/v1/files/upload/init', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      filename: file.name,
      fileSize: file.size,
      contentType:
        file.type || (file.name.toLowerCase().endsWith('.zip') ? 'application/zip' : 'text/csv'),
    }),
  });

  // Step 2: Upload bytes directly from the browser to GCS via chunked resumable upload.
  // gcs-browser-upload handles chunking, retry, and persists the session to localStorage
  // keyed by fileId so uploads can resume if the user navigates away mid-upload.
  const upload = new GcsUpload({
    id: fileDto.id,
    url: uploadUrl,
    file,
    chunkSize: 256 * 1024, // 256 KB chunks
    onChunkUpload: (info: { uploadedBytes: number; totalBytes: number }) => {
      onProgress?.(Math.round((info.uploadedBytes / info.totalBytes) * 100));
    },
  });

  try {
    await upload.start();
  } catch (err) {
    const msg = err instanceof Error ? err.message : '';
    logger.error('GCS resumable upload failed', 'DataConduitClient', { filename: file.name, err });
    if (msg === 'Network error' || msg === 'Failed to fetch') {
      throw new Error('Upload failed due to a connection error. Please try again.');
    }
    throw err;
  }

  return job;
}

export const DEFAULT_PAGE_SIZE = 50;

export async function listJobs(
  limit = DEFAULT_PAGE_SIZE,
  offset = 0
): Promise<PaginatedList<JobConfigDto>> {
  const headers = await buildHeaders('application/json');
  return dcFetch<PaginatedList<JobConfigDto>>(`/api/v1/jobs?limit=${limit}&offset=${offset}`, {
    headers,
  });
}

export async function getJob(id: string): Promise<JobConfigDto> {
  const headers = await buildHeaders('application/json');
  return dcFetch<JobConfigDto>(`/api/v1/jobs/${id}`, { headers });
}

export async function getLayers(
  jobId: string,
  limit = DEFAULT_PAGE_SIZE,
  offset = 0
): Promise<PaginatedList<LayerWithConfigDto>> {
  const headers = await buildHeaders();
  return dcFetch<PaginatedList<LayerWithConfigDto>>(
    `/api/v1/jobs/${jobId}/layers?limit=${limit}&offset=${offset}`,
    { headers }
  );
}

export async function previewLayer(
  jobId: string,
  layerId: string,
  queryFilter: QueryFilter | null = null,
  limit = 20,
  offset = 0
): Promise<PreviewDto> {
  const headers = await buildHeaders('application/json');
  return dcFetch<PreviewDto>(`/api/v1/jobs/${jobId}/layers/${layerId}/preview`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ queryFilter, limit, offset }),
  });
}

export async function saveLayerConfig(
  jobId: string,
  layerId: string,
  payload: SaveLayerConfigPayload
): Promise<LayerWithConfigDto> {
  const headers = await buildHeaders('application/json');
  return dcFetch<LayerWithConfigDto>(`/api/v1/jobs/${jobId}/layers/${layerId}/config`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload),
  });
}

export async function markConfigured(jobId: string): Promise<{ ok: boolean; tablesExist: boolean }> {
  const headers = await buildHeaders('application/json');
  return dcFetch<{ ok: boolean; tablesExist: boolean }>(`/api/v1/jobs/${jobId}/configure`, { method: 'POST', headers });
}

export interface RunJobOptions {
  deleteOnRun?: boolean;
  dropRemovedColumns?: boolean;
  appendMode?: boolean;
}

export async function runJob(jobId: string, options: RunJobOptions = {}): Promise<void> {
  const headers = await buildHeaders('application/json');
  await dcFetch<{ ok: boolean }>(`/api/v1/jobs/${jobId}/run`, {
    method: 'POST',
    headers,
    body: JSON.stringify(options),
  });
}

export type JobRunStatus = 'completed' | 'failed';
export type BqOperationType = 'insert_select' | 'merge' | 'merge_with_delete' | 'truncate_load';

export interface JobRunResultDto {
  id: string;
  jobLayerConfigId: string;
  bqDataset: string;
  bqTable: string;
  status: JobRunStatus;
  rowsWritten: number | null;
  rowsAffected: number | null;
  bqJobId: string | null;
  errorMessage: string | null;
  errorReason: string | null;
  operationType: BqOperationType;
  startedAt: string;
  completedAt: string | null;
}

export async function getJobRuns(
  jobId: string,
  limit = DEFAULT_PAGE_SIZE,
  offset = 0
): Promise<PaginatedList<JobRunResultDto>> {
  const headers = await buildHeaders();
  return dcFetch<PaginatedList<JobRunResultDto>>(
    `/api/v1/jobs/${jobId}/runs?limit=${limit}&offset=${offset}`,
    { headers }
  );
}

export interface JobTableLayerDto {
  id: string;
  displayName: string;
  rowsWritten: number | null;
}

export interface JobTableDataDto {
  layers: JobTableLayerDto[];
  data: {
    columns: string[];
    rows: Record<string, unknown>[];
    limit: number;
    offset: number;
  };
}

export async function getJobTableData(
  jobId: string,
  runResultId?: string,
  limit = 100,
  offset = 0
): Promise<JobTableDataDto> {
  const headers = await buildHeaders();
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (runResultId) params.set('runResultId', runResultId);
  return dcFetch<JobTableDataDto>(`/api/v1/jobs/${jobId}/data?${params.toString()}`, { headers });
}
